import { OnboardingResponse, RequestPhoneVerificationPinResponse } from '@shared/api';
import { ExchangesEnum } from '@shared/enums';

import { createMachine } from 'xstate';

import { AddressAndCitizenshipStep, IdVerificationStep, NameAndDateOfBirthStep } from './components/OnboardingSteps';
import { CongratulationsStep } from './components/OnboardingSteps/CongratulationsStep';
import { DepositFundsStep } from './components/OnboardingSteps/DepositFundsStep';
import { EmailVerificationStep } from './components/OnboardingSteps/EmailVerificationStep';
import { InvestingPlansStep } from './components/OnboardingSteps/InvestingPlansStep';
import { MobileVerificationStep } from './components/OnboardingSteps/MobileVerificationStep';
import { OccupationIncomeStep } from './components/OnboardingSteps/OccupationIncomeStep';
import { QuickIntroStep } from './components/OnboardingSteps/QuickIntroStep';
import {
  EmailCodeVerificationData,
  MobileCodeVerificationData,
  OnboardingStepId,
  VerificationData,
  AssetMigratorData,
} from './types/Onboarding.types';

export interface Context {
  completedSteps: Partial<Record<OnboardingStepId, boolean>>; // Map of IDs of completed steps
  verificationData: Partial<VerificationData>;
  mobileCodeVerificationData: MobileCodeVerificationData;
  emailCodeVerificationData: EmailCodeVerificationData;
  assetMigratorData: AssetMigratorData;
}

export type Event =
  | { type: 'NEXT'; data?: Partial<VerificationData> }
  | { type: 'BACK' }
  | { type: 'COMPLETE_STEP'; stepId: string }
  | { type: 'SAVE_AND_EXIT' }
  | { type: 'ID_VERIFICATION_IN_REVIEW' }
  | { type: 'ID_VERIFICATION_COMPLETE' }
  | { type: 'VERIFICATION_ERROR' }
  | { type: 'SKIP' }
  | { type: 'MOBILE_CODE_INPUT'; code: string }
  | { type: 'MOBILE_CODE_RESENT' }
  | { type: 'EMAIL_CODE_INPUT'; code: string }
  | { type: 'EMAIL_CODE_RESENT' }
  | { type: 'CHOOSE_ASSETMIGRATOR' }
  | { type: 'CHOOSE_CRYPTO' }
  | { type: 'MIGRATION_CONFIRMED'; exchange: ExchangesEnum };

export type Services = {
  fetchOnboardingData: {
    data: OnboardingResponse;
  };
  setupGreenID: {
    data: string;
  };
  sendEmailCode: {
    data: void;
  };
  verifyEmailCode: {
    data: void;
  };
  sendMobileCode: {
    data: RequestPhoneVerificationPinResponse;
  };
  verifyMobileCode: {
    data: void;
  };
};

export const onboardingMachine = createMachine({
  context: {
    completedSteps: {},
    verificationData: {},
    mobileCodeVerificationData: { userInputCode: '', error: '' },
    emailCodeVerificationData: { userInputCode: '', error: '' },
    assetMigratorData: { exchange: undefined },
  },
  tsTypes: {} as import('./Onboarding.machine.typegen').Typegen0,
  schema: { context: {} as Context, events: {} as Event, services: {} as Services },
  predictableActionArguments: true,
  id: 'onboarding',
  initial: 'loading',
  states: {
    loading: {
      initial: 'fetchOnboardingData',
      states: {
        fetchOnboardingData: {
          id: 'fetchOnboardingData',
          invoke: {
            src: 'fetchOnboardingData',
            onDone: {
              target: 'findStartingStep',
              actions: ['prefillProfileData'],
            },
            onError: {
              target: 'findStartingStep',
            },
          },
        },
        findStartingStep: {
          always: [
            {
              target: '#quickIntro',
              cond: 'shouldShowQuickIntro',
            },
            {
              target: '#jumpToPosition',
              actions: ['completeQuickIntro'],
            },
          ],
        },
      },
    },
    quickIntro: {
      id: 'quickIntro',
      on: {
        NEXT: {
          target: '#jumpToPosition',
        },
      },
      meta: {
        step: true,
        Component: QuickIntroStep,
      },
    },

    jumpToPosition: {
      id: 'jumpToPosition',
      always: [
        {
          target: '#emailVerification',
          cond: 'hasNotCompletedEmailStep',
        },
        {
          target: '#mobileVerification',
          cond: 'hasNotCompletedMobileStep',
        },
        {
          target: '#setupProfile.nameAndDob',
          cond: 'hasNotCompletedSetupProfileNameAndDob',
        },
        {
          target: '#setupProfile.nameAndDob',
          cond: 'hasNotCompletedSetupProfileAddress',
        },
        {
          target: '#setupProfile.nameAndDob',
          cond: 'hasNotCompletedSetupProfileInvestingPlans',
        },
        {
          target: '#setupProfile.nameAndDob',
          cond: 'hasNotCompletedSetupProfileOccupationIncome',
        },
        {
          target: '#idVerification',
        },
      ],
    },

    emailVerification: {
      id: 'emailVerification',
      initial: 'sendEmailCode',
      states: {
        sendEmailCode: {
          invoke: {
            src: 'sendEmailCode',
            onDone: {
              target: 'pendingCode',
              actions: ['clearEmailInputCodeError'],
            },
            onError: {
              target: 'pendingCode',
              actions: ['setEmailInputCodeError'],
            },
          },
        },
        pendingCode: {
          on: {
            EMAIL_CODE_INPUT: {
              target: 'verifyEmailCode',
              actions: ['setEmailInputCode'],
            },
            EMAIL_CODE_RESENT: {
              target: 'sendEmailCode',
            },
          },
        },
        verifyEmailCode: {
          invoke: {
            src: 'verifyEmailCode',
            onDone: {
              target: 'verifiedEmail',
              actions: ['clearEmailInputCodeError'],
            },
            onError: {
              target: 'pendingCode',
              actions: ['setEmailInputCodeError'],
            },
          },
        },
        verifiedEmail: {
          on: {
            NEXT: {
              target: '#mobileVerification',
              actions: ['completeEmailVerification'],
            },
          },
        },
      },
      on: {
        BACK: {
          target: '#quickIntro',
        },
      },
      meta: {
        step: true,
        Component: EmailVerificationStep,
        showMobileTitle: false,
      },
    },

    mobileVerification: {
      id: 'mobileVerification',
      initial: 'sendMobileCode',
      states: {
        checkAlreadyVerifiedLocal: {
          always: [
            {
              target: 'sendMobileCode',
              cond: 'hasNotCompletedMobileStep',
            },
            {
              target: '#setupProfile',
            },
          ],
        },
        sendMobileCode: {
          invoke: {
            src: 'sendMobileCode',
            onDone: {
              target: 'checkAlreadyVerifiedRemote',
              actions: ['processSendMobileCodeResponse'],
            },
            onError: {
              target: 'pendingCode',
              actions: ['setMobileInputCodeError'],
            },
          },
        },
        checkAlreadyVerifiedRemote: {
          always: [
            {
              target: 'pendingCode',
              actions: ['clearMobileInputCodeError'],
              cond: 'hasNotCompletedMobileStep',
            },
            {
              target: '#setupProfile',
            },
          ],
        },
        pendingCode: {
          on: {
            MOBILE_CODE_INPUT: {
              target: 'verifyMobileCode',
              actions: ['setMobileInputCode'],
            },
            MOBILE_CODE_RESENT: {
              target: 'sendMobileCode',
            },
          },
        },
        verifyMobileCode: {
          invoke: {
            src: 'verifyMobileCode',
            onDone: {
              target: 'verifiedMobile',
              actions: ['clearMobileInputCodeError'],
            },
            onError: {
              target: 'pendingCode',
              actions: ['setMobileInputCodeError'],
            },
          },
        },
        verifiedMobile: {
          on: {
            NEXT: {
              target: '#setupProfile',
              actions: ['completeMobileVerification'],
            },
          },
        },
      },
      on: {
        BACK: {
          target: '#quickIntro',
        },
      },
      meta: {
        step: true,
        Component: MobileVerificationStep,
        showMobileTitle: false,
      },
    },

    setupProfile: {
      id: 'setupProfile',
      initial: 'nameAndDob',
      meta: {
        step: true,
      },
      states: {
        nameAndDob: {
          on: {
            NEXT: {
              target: 'address',
              actions: ['assignVerificationData', 'syncVerificationData'],
            },
          },
          meta: {
            step: true,
            Component: NameAndDateOfBirthStep,
          },
        },
        address: {
          on: {
            BACK: {
              target: 'nameAndDob',
            },
            NEXT: {
              target: 'investingPlans',
              actions: ['assignVerificationData', 'syncVerificationData'],
            },
          },
          meta: {
            step: true,
            Component: AddressAndCitizenshipStep,
          },
        },
        investingPlans: {
          on: {
            BACK: {
              target: 'address',
            },
            NEXT: [
              {
                target: '#idVerification',
                actions: ['assignVerificationData', 'syncVerificationData'],
                cond: 'isOccupationIncomeStepDisabled',
              },
              {
                target: 'occupationIncome',
                actions: ['assignVerificationData', 'syncVerificationData'],
              },
            ],
          },
          meta: {
            step: true,
            Component: InvestingPlansStep,
          },
        },
        occupationIncome: {
          on: {
            BACK: {
              target: 'investingPlans',
            },
            NEXT: {
              target: '#idVerification',
              actions: ['assignVerificationData', 'syncVerificationData'],
            },
          },
          meta: {
            step: true,
            Component: OccupationIncomeStep,
          },
        },
      },
    },

    idVerification: {
      id: 'idVerification',
      initial: 'checkVerificationState',
      meta: {
        step: true,
        Component: IdVerificationStep,
        showMobileTitle: false,
      },
      states: {
        checkVerificationState: {
          always: [
            { target: 'setupGreenID', cond: 'noGreenIDRef' },
            { target: 'verificationError', cond: 'isVerificationErrored' },
            { target: 'verificationInReview', cond: 'isVerificationInReview' },
            { target: '#depositFunds', cond: 'hasCompletedVerification' },
            { target: 'verificationStart' },
          ],
        },
        setupGreenID: {
          invoke: {
            src: 'setupGreenID',
            onDone: {
              target: 'verificationStart',
            },
            onError: {
              target: 'verificationError',
            },
          },
        },
        verificationStart: {
          on: {
            BACK: {
              actions: ['redirectToDashboard'],
            },
            NEXT: 'verificationInProgress',
          },
        },
        verificationInProgress: {
          on: {
            ID_VERIFICATION_IN_REVIEW: 'verificationInReview',
            ID_VERIFICATION_COMPLETE: {
              target: '#depositFunds',
              actions: ['completeVerification'],
            },
          },
        },
        verificationInReview: {},
        verificationError: {},
      },
      on: {
        VERIFICATION_ERROR: {
          target: 'idVerification.verificationError',
        },
      },
    },

    depositFunds: {
      id: 'depositFunds',
      initial: 'checkResidency',
      meta: {
        step: true,
        hideContinueButton: true,
      },
      states: {
        // TODO: check this line commit when we have a decision around jumio in onboarding flow: DepositFundsSelect
        checkResidency: {
          always: [
            { target: '#congratulations.skippedDeposit', cond: 'isNzResidentOrEntitySignUp' },
            { target: 'depositFiat', cond: 'isNotNzResident' },
          ],
        },
        depositFiat: {
          meta: {
            step: true,
            hidden: true,
            Component: DepositFundsStep,
          },
          on: {
            NEXT: {
              target: '#congratulations.deposited',
            },
            SKIP: {
              target: '#congratulations.skippedDeposit',
            },
          },
        },
      },
    },

    congratulations: {
      id: 'congratulations',
      initial: 'deposited',
      meta: {
        step: false,
      },
      states: {
        deposited: {
          meta: {
            step: true,
            hidden: true,
            Component: CongratulationsStep,
          },
        },

        skippedDeposit: {
          meta: {
            step: true,
            hidden: true,
            Component: CongratulationsStep,
          },
        },

        idIssue: {
          meta: {
            step: true,
            hidden: true,
            Component: CongratulationsStep,
          },
        },

        assetsMigrating: {
          meta: {
            step: true,
            hidden: true,
            Component: CongratulationsStep,
          },
        },
      },
    },
  },
  on: {
    COMPLETE_STEP: {
      actions: ['completeStep'],
    },
    SAVE_AND_EXIT: {
      actions: ['redirectToDashboard'],
    },
  },
});
